import React from 'react'
import type {
  FieldErrorProps,
  GroupProps,
  InputProps as RACInputProps,
  LabelProps,
  TextAreaProps,
} from 'react-aria-components'
import {
  composeRenderProps,
  Group,
  FieldError as RACFieldError,
  Input as RACInput,
  Label as RACLabel,
  Text,
  TextArea as RACTextArea,
  TextProps,
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import { composeTailwindRenderProps, focusRing } from '../../utils'

export interface InputProps extends RACInputProps {
  variant?: 'default' | 'grouped'
}

export function Label(props: LabelProps) {
  return (
    <RACLabel
      {...props}
      className={twMerge(
        'block w-fit cursor-default text-sm font-semibold leading-6 text-default-50',
        props.className
      )}
    />
  )
}

export function Description(props: TextProps) {
  return (
    <Text
      {...props}
      slot="description"
      className={twMerge('text-sm text-default-600', props.className)}
    />
  )
}

export function FieldError(props: FieldErrorProps) {
  return (
    <RACFieldError
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'text-sm text-danger-600'
      )}
    />
  )
}

export const fieldBorderStyles = tv({
  variants: {
    isFocusWithin: {
      false: 'border-default-300',
      true: 'border-primary-600 ',
    },
    isInvalid: {
      true: 'border-danger-600 ',
    },
    isDisabled: {
      true: 'border-default-200 ',
    },
  },
})

export const fieldGroupStyles = tv({
  extend: focusRing,
  base: 'group flex items-center h-9 bg-default-1000 border rounded-md overflow-hidden',
  variants: fieldBorderStyles.variants,
})

export function FieldGroup(props: GroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        fieldGroupStyles({ ...renderProps, className })
      )}
    />
  )
}

const inputStyles = tv({
  extend: focusRing,
  base: 'border block w-full flex-0 min-w-0 outline outline-0 px-3.5 py-2 text-default-50 shadow-sm  placeholder:text-default-400 disabled:text-default-200 ',
  variants: {
    variant: {
      default: 'rounded-md',
      grouped: 'border-0',
    },
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    ...fieldBorderStyles.variants,
  },
  defaultVariants: {
    variant: 'default',
  },
})

export function Input(props: InputProps) {
  return (
    <RACInput
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        inputStyles({ ...renderProps, className })
      )}
    />
  )
}

export function TextAreaInput(props: TextAreaProps) {
  return (
    <RACTextArea
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'block w-full min-w-0 flex-1 rounded-md px-3.5 py-2 text-default-50 shadow-sm outline outline-0 placeholder:text-default-400 disabled:text-default-200'
      )}
    />
  )
}
